import { Skeleton } from '@eureca/ui';
import { TriangleAlertIcon } from '@eureca/ui/icons';
import { StarIcon } from '@eureca/ui/icons';
import { GavelIcon } from './icons';
import type { EvaluationCardData } from './candidate-evaluations';
import { PotentialCriteriaRows } from './PotentialCriteriaRows';

/**
 * EU-686 (spec 068) — timeline vertical READ-ONLY do histórico de avaliações
 * (RN-07/08): mais recente primeiro (ordem já vem do backend), conector entre
 * itens, ícone+título por tipo (Deliberação com ícone DISTINTO — HAV-009),
 * grid de campos na ordem RN-09/10 e comentário por extenso (label Medium,
 * texto regular, sem truncamento — RN-12). Nenhuma ação, nenhuma navegação
 * (RN-18). Puro: sem hooks.
 */
export function EvaluationsTimeline({ cards }: { cards: EvaluationCardData[] }) {
  return (
    <ol data-testid="candidate-profile-evaluations" className="flex flex-col">
      {cards.map((card, index) => (
        <li key={card.key} className="relative flex flex-row gap-4 pb-8 last:pb-0">
          {/* Conector da timeline (RN-07) — liga este item ao próximo. */}
          {index < cards.length - 1 ? (
            <span
              aria-hidden
              className="absolute left-4 top-8 h-[calc(100%-2rem)] w-px -translate-x-1/2 bg-border"
            />
          ) : null}
          <span
            data-testid={
              card.kind === 'deliberation'
                ? 'evaluation-icon-deliberation'
                : 'evaluation-icon-evaluation'
            }
            className="z-10 flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-secondary text-primary"
          >
            {card.kind === 'deliberation' ? (
              <GavelIcon className="size-4" />
            ) : (
              <StarIcon className="size-4" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <h3 className="pt-1.5 text-base font-medium leading-none text-foreground">
              {card.title}
            </h3>
            <dl className="grid grid-cols-1 gap-x-10 gap-y-2 sm:grid-cols-2">
              {card.fields.map((field) => (
                <div
                  key={field.label}
                  className="flex flex-row items-baseline justify-between gap-6"
                >
                  <dt className="shrink-0 text-sm leading-5 text-muted-foreground">
                    {field.label}
                  </dt>
                  <dd className="min-w-0 break-words text-right text-sm font-medium leading-5 text-foreground">
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
            {/* DEV-286 — notas de potencial entre os metadados e as respostas em
                texto, só quando a avaliação as tem (AC-17/AC-19). */}
            {card.potentialCriteria ? (
              <PotentialCriteriaRows scores={card.potentialCriteria} />
            ) : null}
            {card.answers.map((answer) => (
              <p key={answer.label} className="text-sm leading-6 text-foreground">
                <span className="font-medium">{answer.label}: </span>
                <span className="font-normal">{answer.value}</span>
              </p>
            ))}
            {card.comment ? (
              <p className="text-sm leading-6 text-foreground">
                {/* RN-12 — label Medium, texto regular, por extenso. */}
                <span className="font-medium">Comentário: </span>
                <span className="font-normal">{card.comment}</span>
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** RN-16 — skeleton restrito à coluna de conteúdo. */
export function EvaluationsSkeleton() {
  return (
    <div data-testid="candidate-profile-evaluations-loading" className="flex flex-col gap-8">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-row gap-4">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-full max-w-md" />
            <Skeleton className="h-3 w-full max-w-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * RN-14 — estado vazio (copies literais do card), 100% fiel ao Figma
 * 15762-69082 (retorno de QA EU-686): círculo `bg-muted` 64px com o
 * `triangle-alert` 28px em `foreground` acima do título.
 */
export function EvaluationsEmpty() {
  return (
    <div
      data-testid="candidate-profile-evaluations-empty"
      className="flex flex-col items-center gap-5 py-12 text-center"
    >
      <span
        data-testid="candidate-profile-evaluations-empty-icon"
        className="flex size-16 items-center justify-center rounded-full bg-muted p-2 text-foreground"
      >
        <TriangleAlertIcon className="size-7" aria-hidden />
      </span>
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-medium tracking-[-0.6px] text-foreground">Sem avaliações</h3>
        <p className="text-base leading-6 text-muted-foreground">
          Esse candidato ainda não recebeu nenhuma avaliação.
        </p>
      </div>
    </div>
  );
}

/**
 * RN-15 — erro LOCALIZADO da carga do histórico (copies literais do card),
 * 100% fiel ao Figma 15762-69160: círculo `bg-destructive-focus` (o coral
 * translúcido do token custom/destructive-focus) com o `triangle-alert` em
 * `destructive` acima do título.
 */
export function EvaluationsError() {
  return (
    <div
      data-testid="candidate-profile-evaluations-error"
      role="alert"
      className="flex flex-col items-center gap-5 py-12 text-center"
    >
      <span
        data-testid="candidate-profile-evaluations-error-icon"
        className="flex size-16 items-center justify-center rounded-full bg-destructive-focus p-2 text-destructive"
      >
        <TriangleAlertIcon className="size-7" aria-hidden />
      </span>
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-medium tracking-[-0.6px] text-foreground">Erro inesperado</h3>
        <p className="text-base leading-6 text-muted-foreground">
          Não foi possível carregar as avaliações desse candidato. Tente novamente mais tarde.
        </p>
      </div>
    </div>
  );
}
